import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Clock, DollarSign, Bell } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Services = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyingClass, setNotifyingClass] = useState(null);
  const [groupContent, setGroupContent] = useState({
    title: 'Group & On-Site Training',
    subtitle: 'Need training for your team? We come to you!',
    description: 'PUSH Training - NC offers customized on-site training for businesses, schools, healthcare facilities, and organizations. Perfect for:',
    bullet_points: 'Healthcare facilities and clinics\nSchools and daycare centers\nCorporate offices and businesses\nFitness centers and gyms\nCommunity organizations\nGroups of 5 or more',
    cta_text: 'Request Group Quote'
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchClasses();
    axios.get(`${API}/content/services_group`).then(r => setGroupContent(prev => ({ ...prev, ...r.data }))).catch(console.error);
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyMe = async (classId) => {
    if (!notifyEmail) {
      toast({
        title: "Email Required",
        description: "Please enter your email address.",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(`${API}/notify-requests`, {
        classId,
        email: notifyEmail
      });
      toast({
        title: "Success!",
        description: "We'll notify you when this class becomes available.",
      });
      setNotifyingClass(null);
      setNotifyEmail('');
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (course) => {
    if (course.maintenanceMode) {
      return (
        <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">
          Check Back Soon
        </Badge>
      );
    }
    if (course.comingSoon) {
      return (
        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
          Coming Soon
        </Badge>
      );
    }
    if (course.available) {
      const remaining = course.maxSeats ? course.maxSeats - (course.bookedSeats || 0) : null;
      if (remaining !== null && remaining <= 0) {
        return (
          <Badge className="bg-gray-200 text-gray-600 hover:bg-gray-200">
            Class Full
          </Badge>
        );
      }
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
          Available
        </Badge>
      );
    }
    return (
      <Badge className="bg-gray-200 text-gray-600 hover:bg-gray-200">
        Unavailable
      </Badge>
    );
  };

  const isBookable = (course) => {
    if (course.maintenanceMode || course.comingSoon || !course.available) {
      return false;
    }
    if (course.maxSeats) {
      const remaining = course.maxSeats - (course.bookedSeats || 0);
      return remaining > 0;
    }
    return true;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading classes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-50 to-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our <span className="text-red-600">Training Programs</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Professional certification courses designed to give you the skills and confidence to save lives.
          </p>
        </div>
      </section>

      {/* Classes Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {classes.map((course) => (
              <Card key={course.id} className="border-2 hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                        <CardTitle className="text-2xl md:text-3xl">{course.name}</CardTitle>
                        {getStatusBadge(course)}
                      </div>
                      <CardDescription className="text-base md:text-lg">
                        {course.description}
                      </CardDescription>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-2">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock size={20} />
                        <span className="font-medium">{course.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign size={24} className="text-red-600" />
                        <span className="text-3xl font-bold text-red-600">
                          {course.price}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">What You'll Learn:</h4>
                      <ul className="space-y-2">
                        {course.highlights.map((highlight, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Check className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                            <span className="text-gray-600">{highlight}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex items-end">
                      {isBookable(course) ? (
                        <Button 
                          className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6"
                          onClick={() => window.open(course.booking_url || 'https://zbooking.us/xu8Qz', '_blank')}
                        >
                          Book This Class
                        </Button>
                      ) : (
                        <Button 
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                          onClick={() => setNotifyingClass(course.id)}
                        >
                          <Bell size={20} className="mr-2" />
                          Notify Me When Available
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Group Training Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-red-200 bg-white">
            <CardHeader>
              <CardTitle className="text-3xl">{groupContent.title}</CardTitle>
              <CardDescription className="text-lg">{groupContent.subtitle}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-600 text-lg">{groupContent.description}</p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(groupContent.bullet_points || '').split('\n').filter(b => b.trim()).map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                      <span className="text-gray-600">{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4">
                  <Link to="/contact">
                    <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                      {groupContent.cta_text}
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Certified?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Choose a class above or contact us to discuss your training needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100">
                Contact Us
              </Button>
            </Link>
            <Link to="/faq">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-red-700">
                View FAQ
              </Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Notify Me Dialog */}
      <Dialog open={notifyingClass !== null} onOpenChange={() => setNotifyingClass(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Get Notified When Available</DialogTitle>
            <DialogDescription>
              Enter your email and we'll notify you when this class becomes available.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Input
                type="email"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="your.email@example.com"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNotifyingClass(null)}>
                Cancel
              </Button>
              <Button 
                onClick={() => handleNotifyMe(notifyingClass)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Notify Me
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
